import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Building2, FileCheck2, Truck, Mail, LogOut, Menu } from "lucide-react";

const SUB_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/profile", label: "Company Profile", icon: Building2, testid: "nav-profile" },
  { to: "/documents", label: "Documents", icon: FileCheck2, testid: "nav-documents" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-dashboard", end: true },
  { to: "/admin/carriers", label: "Carriers", icon: Truck, testid: "nav-admin-carriers" },
  { to: "/admin/emails", label: "Email Log", icon: Mail, testid: "nav-admin-emails" },
];

function Logo() {
  return (
    <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
      <img src="/tagxpress-logo.png" alt="TAG Xpress logo" data-testid="sidebar-logo"
        className="h-10 w-auto rounded-md bg-white px-2.5 py-1.5" />
      <div className="text-[10px] uppercase tracking-[0.18em] leading-relaxed text-slate-400">
        Carrier Compliance<br />Portal
      </div>
    </div>
  );
}

function NavItems({ items, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3 py-4">
      {items.map(({ to, label, icon: Icon, testid, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-white/10 text-white border-l-2 border-brand"
                : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "admin" ? ADMIN_NAV : SUB_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 flex-col bg-navy md:flex" data-testid="sidebar">
        <Logo />
        <NavItems items={nav} />
        <div className="mt-auto border-t border-white/10 p-4">
          <div className="mb-3 px-1">
            <div className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">
              {user?.business_name || user?.email}
            </div>
            <div className="truncate text-xs text-slate-400">{user?.email}</div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-brand">
              {user?.role === "admin" ? "TAG Xpress Admin" : "Subcontractor"}
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start gap-2 text-slate-300 hover:bg-white/5 hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-border bg-navy px-4 py-3 md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-button" variant="ghost" size="icon" aria-label="Open menu" className="text-white hover:bg-white/10">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-navy p-0">
              <Logo />
              <NavItems items={nav} onNavigate={() => setOpen(false)} />
              <div className="border-t border-white/10 p-4">
                <Button
                  data-testid="mobile-logout-button"
                  variant="ghost"
                  className="w-full justify-start gap-2 text-slate-300 hover:bg-white/5 hover:text-white"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <img src="/tagxpress-logo.png" alt="TAG Xpress logo" data-testid="mobile-header-logo"
            className="h-8 w-auto rounded bg-white px-1.5 py-0.5" />
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Compliance Portal</div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
